import React, { useState, useEffect } from "react";
import { List, Radio } from "antd";
import { useSelector } from "react-redux";
import PhoneNumberHeader from "./PhoneNumberHeader";
import CustomTypography from "../../../commonComponents/CustomTypography";
import CustomSkeleton from "../../../commonComponents/CustomSkeleton";
import { getItemIdentifier } from "./gotoHelpers";
import useLang from "../../../../hooks/useLang";
import useLocation from "../../../../hooks/useLocation";
import { getCompanyId } from "../../../../redux/slices/companySlice";

const AssignLocationDrawer = ({
  phoneData,
  extensionData,
  userData,
  deviceData,
  onLocationSelect,
}) => {
  const [gotoLang] = useLang("GoTo");
  const companyId = useSelector(getCompanyId);
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  // Fetch locations using the hook
  const { data: locationsData, loading } = useLocation({
    variables: { companyId, isAllLocations: true },
    skip: !companyId,
    fetchPolicy: "cache-first",
  });

  const locations = locationsData?.getLocations || [];

  const recordLocation =
    phoneData?.originalLocation ||
    extensionData?.originalLocation ||
    userData?.originalLocation ||
    deviceData?.originalLocation;

  // Get current location data from all 4 types: phone, extension, user, device
  const currentLocationLevel =
    phoneData?.originalLocation?.id ||
    extensionData?.originalLocation?.id ||
    userData?.originalLocation?.id ||
    deviceData?.originalLocation?.id;

  // Find the matching location object
  const currentLocation = locations.find(
    loc => loc.id === currentLocationLevel || loc.id === parseInt(currentLocationLevel, 10)
  );

  // Set initial selected location
  useEffect(() => {
    if (currentLocation) {
      setSelectedLocationId(currentLocation.id);
    }
  }, [currentLocation]);

  // Get the item identifier (number, name, etc.) for the header
  const itemIdentifier = getItemIdentifier(phoneData, extensionData, userData, deviceData);

  const handleLocationSelect = locId => {
    setSelectedLocationId(locId);
    const selectedLocation = locations.find(loc => loc.id === locId);
    if (onLocationSelect) {
      onLocationSelect(selectedLocation);
    }
  };

  return (
    <div>
      {/* Header showing current assignment */}
      <div className="mb-6">
        <PhoneNumberHeader
          phoneNumber={itemIdentifier}
          assignedTo={recordLocation?.title || null}
          assignedToRole={null}
          label={gotoLang.currentlyAssignedTo}
        />
      </div>

      <div className="my-6">
        <CustomTypography variant="text" strong>
          {gotoLang.selectLocationToAssign || "Select a location to assign"}
        </CustomTypography>
      </div>

      {/* Locations List - Scrollable with fixed height */}
      <div className="overflow-y-auto pr-2" style={{ height: "calc(100vh - 348px)" }}>
        {loading ? (
          <CustomSkeleton
            skeletonCount={5}
            skeletonWidth="34rem"
            skeletonHeight="3rem"
            skeletonMarginBottom="15px"
          />
        ) : (
          <List
            dataSource={locations}
            renderItem={location => (
              <List.Item className="!p-0 !border-0 !w-full">
                <div className="w-full">
                  <div
                    onClick={() => handleLocationSelect(location.id)}
                    className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all ${
                      selectedLocationId === location.id
                        ? "bg-[#E6F4FF] border border-solid border-[#91CAFF]"
                        : "bg-white hover:bg-[#F5F5F5]"
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <Radio checked={selectedLocationId === location.id} />
                      <CustomTypography.Body className="!text-black !font-normal">
                        {location.title}
                      </CustomTypography.Body>
                    </div>
                  </div>
                </div>
              </List.Item>
            )}
          />
        )}
      </div>
    </div>
  );
};

export default AssignLocationDrawer;
